import React, { useEffect, useRef, useState } from 'react';
import { Download, Printer, RefreshCw, ChevronDown } from 'lucide-react';
import Button from '../components/Button';
import SegmentToggle from './SegmentToggle';
import PeriodSelect from './PeriodSelect';

/**
 * Universal report toolbar shared by every accounting-report tab.
 * Left group: [SegmentToggle?] + PeriodSelect + [Comparative checkbox?] + [Refresh icon]
 * Right group: Export dropdown (CSV, PDF) + Print — both ghost/secondary, no primary color.
 *
 * Every slot is opt-in via props; a tab's own config decides what to pass so
 * only the relevant controls render (see each report page's `toolbarConfig`).
 */
const ReportToolbar = ({
  showSegment = false,
  segment = 'ALL',
  onSegmentChange,

  showPeriod = true, // set false for reports whose filter shape isn't a date range (e.g. fiscal quarter + month)
  period,
  onPeriodChange,
  fiscalYear,

  showComparative = false,
  comparative = false,
  onComparativeChange,

  onExportCSV,
  onExportPDF,
  onPrint,

  onRefresh,
  refreshing = false,

  extra, // slot for a tab's own bespoke filters (account picker, aging method, etc.)
}) => {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-ink-line rounded-lg px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        {showSegment && <SegmentToggle value={segment} onChange={onSegmentChange} />}

        {showPeriod && (
          <PeriodSelect
            preset={period?.preset}
            startDate={period?.startDate}
            endDate={period?.endDate}
            fiscalYear={fiscalYear}
            onChange={onPeriodChange}
          />
        )}

        {showComparative && (
          <label className="flex items-center gap-2 text-sm text-ink-body cursor-pointer select-none">
            <input
              type="checkbox"
              checked={comparative}
              onChange={(e) => onComparativeChange(e.target.checked)}
              className="h-4 w-4 rounded border-ink-line text-primary-500 focus:ring-primary-500"
            />
            Comparative
          </label>
        )}

        {extra}

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            aria-label="Refresh"
            title="Refresh"
            className="p-2 rounded-md text-ink-body hover:bg-ink-canvas hover:text-ink transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {(onExportCSV || onExportPDF) && (
          <div className="relative" ref={exportRef}>
            <Button variant="ghost" size="sm" icon={Download} onClick={() => setExportOpen((o) => !o)}>
              Export
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
            {exportOpen && (
              <div className="absolute right-0 mt-1 w-36 bg-white border border-ink-line rounded-md shadow-card-hover z-20 py-1">
                {onExportCSV && (
                  <button
                    type="button"
                    onClick={() => { setExportOpen(false); onExportCSV(); }}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-ink-canvas"
                  >
                    CSV
                  </button>
                )}
                {onExportPDF && (
                  <button
                    type="button"
                    onClick={() => { setExportOpen(false); onExportPDF(); }}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-ink-canvas"
                  >
                    PDF
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {onPrint && (
          <Button variant="ghost" size="sm" icon={Printer} onClick={onPrint}>
            Print
          </Button>
        )}
      </div>
    </div>
  );
};

export default ReportToolbar;
